/**
 * ORBIT — defend a hollow core by rotating on its rim and intercepting inbound balls.
 * Difficulty rises smoothly within each 1000-score block (no hard jumps at multiples).
 */

import { gameRandom } from "./game-rng";

export const ORBIT_SCORE_BLOCK = 1000;

/** Core radius as a fraction of min(canvas w, h). */
export const ORBIT_CORE_RADIUS_FRAC = 0.118;
/** Extra gap outside the core stroke so the cursor sits clearly off the rim. */
export const ORBIT_CURSOR_GAP_REF = 18;

/** Angular response rate (1/s) toward hold/keyboard target speed. */
export const ORBIT_ANGULAR_ACCEL = 36;
/** Hold / keyboard / slide max angular speed (rad/s). */
export const ORBIT_MAX_ANGULAR_SPEED = 9.5;
/** Stop response rate (1/s) when no hold input. */
export const ORBIT_ANGULAR_DRAG = 22;

/** Ball speed (Obstacle units × layoutScale) at score 0 / start of tier 0. */
export const ORBIT_BALL_SPEED_BASE = 0.52;
/** Extra ball speed gained per full 1000-score tier. */
export const ORBIT_BALL_SPEED_PER_TIER = 0.2;

/** Spawn rate (balls/sec) at score 0. */
export const ORBIT_SPAWN_RATE_BASE = 0.48;
/** Extra spawn rate per full 1000-score tier. */
export const ORBIT_SPAWN_RATE_PER_TIER = 0.38;

/** How many balls can spawn in one tick when rate is high (soft cap). */
export const ORBIT_SPAWN_BURST_CAP = 3;

export const ORBIT_INITIAL_BALLS = 1;
/** Lives at run start. */
export const ORBIT_LIVES_START = 3;
/** Hard cap on core lives (bonus every 1000 score). */
export const ORBIT_LIVES_CAP = 5;

type OrbitTuning = {
    coreRadiusFrac: number;
    cursorGapRef: number;
    angularAccel: number;
    maxAngularSpeed: number;
    angularDrag: number;
    ballSpeedBase: number;
    ballSpeedPerTier: number;
    spawnRateBase: number;
    spawnRatePerTier: number;
    livesStart: number;
    livesCap: number;
    lifeBonusEvery: number;
};

const DEFAULT_TUNING: OrbitTuning = {
    coreRadiusFrac: ORBIT_CORE_RADIUS_FRAC,
    cursorGapRef: ORBIT_CURSOR_GAP_REF,
    angularAccel: ORBIT_ANGULAR_ACCEL,
    maxAngularSpeed: ORBIT_MAX_ANGULAR_SPEED,
    angularDrag: ORBIT_ANGULAR_DRAG,
    ballSpeedBase: ORBIT_BALL_SPEED_BASE,
    ballSpeedPerTier: ORBIT_BALL_SPEED_PER_TIER,
    spawnRateBase: ORBIT_SPAWN_RATE_BASE,
    spawnRatePerTier: ORBIT_SPAWN_RATE_PER_TIER,
    livesStart: ORBIT_LIVES_START,
    livesCap: ORBIT_LIVES_CAP,
    lifeBonusEvery: ORBIT_SCORE_BLOCK,
};

let tuning: OrbitTuning = { ...DEFAULT_TUNING };

/** Angle 0 = right; increases counter-clockwise. Start on top of the core. */
let angle = -Math.PI / 2;
let angularVel = 0;
let spawnAcc = 0;
/** Horizontal slide / pointer steer (−1…1), decays each frame. */
let slideSteer = 0;

/** Current life slots (starts at ORBIT_LIVES_START, grows with score up to CAP). */
let livesMax = ORBIT_LIVES_START;
/** Lives lost this run (0…livesMax); slots die right → left. */
let livesLost = 0;
/** 0 = alive mini-cursor, 1 = cross (per slot left → right). */
let lifeLostProgress: number[] = [0, 0, 0];
/** How many 1000-score life bonuses have already been applied this run. */
let lifeBonusGranted = 0;

const ORBIT_REVERSAL_FADE_MS = 1000;
const ORBIT_REVERSAL_ALPHA = 0.3;

type OrbitReversalMark = {
    angle: number;
    bornAt: number;
};

let reversalMarks: OrbitReversalMark[] = [];
/** Last non-zero orbit spin sign (−1 / +1) for direction-change detection. */
let lastOrbitVelSign = 0;

export function setOrbitTuning(opts?: Partial<OrbitTuning>): void {
    tuning = {
        coreRadiusFrac:
            opts?.coreRadiusFrac != null && Number.isFinite(opts.coreRadiusFrac)
                ? Math.max(0.04, Math.min(0.4, opts.coreRadiusFrac))
                : DEFAULT_TUNING.coreRadiusFrac,
        cursorGapRef:
            opts?.cursorGapRef != null && Number.isFinite(opts.cursorGapRef)
                ? Math.max(0, opts.cursorGapRef)
                : DEFAULT_TUNING.cursorGapRef,
        angularAccel:
            opts?.angularAccel != null && Number.isFinite(opts.angularAccel)
                ? Math.max(1, opts.angularAccel)
                : DEFAULT_TUNING.angularAccel,
        maxAngularSpeed:
            opts?.maxAngularSpeed != null && Number.isFinite(opts.maxAngularSpeed)
                ? Math.max(0.5, opts.maxAngularSpeed)
                : DEFAULT_TUNING.maxAngularSpeed,
        angularDrag:
            opts?.angularDrag != null && Number.isFinite(opts.angularDrag)
                ? Math.max(0, opts.angularDrag)
                : DEFAULT_TUNING.angularDrag,
        ballSpeedBase:
            opts?.ballSpeedBase != null && Number.isFinite(opts.ballSpeedBase)
                ? Math.max(0.05, opts.ballSpeedBase)
                : DEFAULT_TUNING.ballSpeedBase,
        ballSpeedPerTier:
            opts?.ballSpeedPerTier != null && Number.isFinite(opts.ballSpeedPerTier)
                ? Math.max(0, opts.ballSpeedPerTier)
                : DEFAULT_TUNING.ballSpeedPerTier,
        spawnRateBase:
            opts?.spawnRateBase != null && Number.isFinite(opts.spawnRateBase)
                ? Math.max(0.05, opts.spawnRateBase)
                : DEFAULT_TUNING.spawnRateBase,
        spawnRatePerTier:
            opts?.spawnRatePerTier != null && Number.isFinite(opts.spawnRatePerTier)
                ? Math.max(0, opts.spawnRatePerTier)
                : DEFAULT_TUNING.spawnRatePerTier,
        livesStart:
            opts?.livesStart != null && Number.isFinite(opts.livesStart)
                ? Math.max(1, Math.min(1000, Math.floor(opts.livesStart)))
                : DEFAULT_TUNING.livesStart,
        livesCap:
            opts?.livesCap != null && Number.isFinite(opts.livesCap)
                ? Math.max(1, Math.min(1000, Math.floor(opts.livesCap)))
                : DEFAULT_TUNING.livesCap,
        lifeBonusEvery:
            opts?.lifeBonusEvery != null && Number.isFinite(opts.lifeBonusEvery)
                ? Math.max(100, Math.floor(opts.lifeBonusEvery))
                : DEFAULT_TUNING.lifeBonusEvery,
    };
    // Keep start ≤ cap after both are resolved.
    if (tuning.livesStart > tuning.livesCap) tuning.livesStart = tuning.livesCap;
}

export function resetOrbitState(): void {
    angle = -Math.PI / 2;
    angularVel = 0;
    spawnAcc = 0;
    slideSteer = 0;
    livesMax = Math.max(1, Math.min(tuning.livesCap, tuning.livesStart));
    livesLost = 0;
    lifeLostProgress = Array.from({ length: livesMax }, () => 0);
    lifeBonusGranted = 0;
    reversalMarks = [];
    lastOrbitVelSign = 0;
}

export function getOrbitLivesMax(): number {
    return livesMax;
}

export function getOrbitLivesRemaining(): number {
    return Math.max(0, livesMax - livesLost);
}

/** Life slot 0 = leftmost … livesMax-1 = rightmost. */
export function isOrbitLifeSlotDead(slotIndex: number): boolean {
    const deadFrom = livesMax - livesLost;
    return slotIndex >= deadFrom;
}

export function getOrbitLifeLostProgress(slotIndex: number): number {
    return lifeLostProgress[slotIndex] ?? 0;
}

/**
 * Each lifeBonusEvery score grants one life bonus:
 * 1) restore a lost life if any (X → cursor again),
 * 2) else add a new slot if under livesCap,
 * 3) else no-op (already at full cap).
 *
 * Example: lose a life at 500 → at 1000 that life regenerates back to start count.
 */
export function syncOrbitLivesFromScore(score: number): void {
    const block = Math.max(100, tuning.lifeBonusEvery);
    const tier = Math.floor(Math.max(0, Number(score) || 0) / block);
    while (lifeBonusGranted < tier) {
        lifeBonusGranted += 1;
        if (livesLost > 0) {
            livesLost -= 1;
        } else if (livesMax < tuning.livesCap) {
            livesMax += 1;
            // Insert a fresh alive life at the left so right-to-left death order stays stable.
            lifeLostProgress.unshift(0);
        }
    }
}

export function tickOrbitLives(dt: number): void {
    const step = dt * 3.2;
    while (lifeLostProgress.length < livesMax) lifeLostProgress.push(0);
    if (lifeLostProgress.length > livesMax) lifeLostProgress.length = livesMax;
    for (let i = 0; i < livesMax; i++) {
        const target = isOrbitLifeSlotDead(i) ? 1 : 0;
        const cur = lifeLostProgress[i] ?? 0;
        if (cur < target) lifeLostProgress[i] = Math.min(1, cur + step);
        else if (cur > target) lifeLostProgress[i] = Math.max(0, cur - step);
    }
}

export function orbitImpactPoint(
    canvasW: number,
    canvasH: number,
    impactAngle: number,
): { x: number; y: number } {
    const cx = canvasW * 0.5;
    const cy = canvasH * 0.5;
    const r = getOrbitCoreRadius(canvasW, canvasH);
    return {
        x: cx + Math.cos(impactAngle) * r,
        y: cy + Math.sin(impactAngle) * r,
    };
}

/**
 * Register a core breach at the ball position. Returns game-over flag and rim impact angle.
 * Removes one life from the rightmost slot still alive.
 */
export function orbitCoreTakeHit(
    bx: number,
    by: number,
    canvasW: number,
    canvasH: number,
): { gameOver: boolean; angle: number } {
    const cx = canvasW * 0.5;
    const cy = canvasH * 0.5;
    const impactAngle = Math.atan2(by - cy, bx - cx);
    livesLost = Math.min(livesMax, livesLost + 1);
    return { gameOver: livesLost >= livesMax, angle: impactAngle };
}

export function getOrbitAngle(): number {
    return angle;
}

export function getOrbitAngularVel(): number {
    return angularVel;
}

/**
 * Ease within a 0–1 block so early score (≈100) is a mild bump,
 * mid-block (≈500) is stronger, and the end reaches the next tier base.
 */
export function orbitBlockEase(local01: number): number {
    const x = Math.max(0, Math.min(1, local01));
    // Control points: score 0, 100, 500, 1000 within the block.
    const pts: Array<[number, number]> = [
        [0, 0],
        [0.1, 0.14],
        [0.5, 0.52],
        [1, 1],
    ];
    for (let i = 0; i < pts.length - 1; i++) {
        const [x0, y0] = pts[i]!;
        const [x1, y1] = pts[i + 1]!;
        if (x <= x1 || i === pts.length - 2) {
            const u = x1 <= x0 ? 1 : (x - x0) / (x1 - x0);
            const s = u * u * (3 - 2 * u);
            return y0 + (y1 - y0) * s;
        }
    }
    return 1;
}

export type OrbitDifficulty = {
    tier: number;
    t: number;
    ballSpeed: number;
    spawnRate: number;
};

export function getOrbitDifficulty(score: number): OrbitDifficulty {
    const s = Math.max(0, Number(score) || 0);
    const tier = Math.floor(s / ORBIT_SCORE_BLOCK);
    const local = (s % ORBIT_SCORE_BLOCK) / ORBIT_SCORE_BLOCK;
    const t = orbitBlockEase(local);
    const ballLo = tuning.ballSpeedBase + tier * tuning.ballSpeedPerTier;
    const ballHi = tuning.ballSpeedBase + (tier + 1) * tuning.ballSpeedPerTier;
    const rateLo = tuning.spawnRateBase + tier * tuning.spawnRatePerTier;
    const rateHi = tuning.spawnRateBase + (tier + 1) * tuning.spawnRatePerTier;
    return {
        tier,
        t,
        ballSpeed: ballLo + (ballHi - ballLo) * t,
        spawnRate: rateLo + (rateHi - rateLo) * t,
    };
}

export function getOrbitCoreRadius(canvasW: number, canvasH: number): number {
    const m = Math.min(Math.max(1, canvasW), Math.max(1, canvasH));
    return m * tuning.coreRadiusFrac;
}

export function getOrbitCursorRadius(
    canvasW: number,
    canvasH: number,
    cursorRadiusPx: number,
    layoutScale: number,
): number {
    const core = getOrbitCoreRadius(canvasW, canvasH);
    return core + cursorRadiusPx + tuning.cursorGapRef * layoutScale;
}

export function orbitCursorPos(
    canvasW: number,
    canvasH: number,
    cursorRadiusPx: number,
    layoutScale: number,
): { x: number; y: number } {
    const cx = canvasW * 0.5;
    const cy = canvasH * 0.5;
    const r = getOrbitCursorRadius(canvasW, canvasH, cursorRadiusPx, layoutScale);
    return {
        x: cx + Math.cos(angle) * r,
        y: cy + Math.sin(angle) * r,
    };
}

/**
 * Feed horizontal pointer/slide as directional steer (not speed).
 * Positive dx (right) → steer right; orbit rate is capped by maxAngularSpeed.
 */
export function feedOrbitSlideDelta(dx: number, layoutScale: number): void {
    if (!Number.isFinite(dx) || dx === 0) return;
    const ls = Math.max(0.5, layoutScale);
    slideSteer += (dx / (70 * ls)) * 1.35;
    slideSteer = Math.max(-1.5, Math.min(1.5, slideSteer));
}

/**
 * @param inputDir −1 = hold left, +1 = hold right, 0 = none (keyboard / side zones).
 * Slide only chooses direction; angular speed is always limited by maxAngularSpeed.
 */
export function tickOrbitMovement(
    dt: number,
    inputDir: number,
    canvasW: number,
    canvasH: number,
    cursorRadiusPx: number,
    layoutScale: number,
): { x: number; y: number } {
    const t = Math.max(0, Math.min(0.05, dt));
    let dir = 0;
    if (inputDir < 0) dir = -1;
    else if (inputDir > 0) dir = 1;
    if (Math.abs(slideSteer) > 0.02) {
        dir = Math.max(-1, Math.min(1, dir + slideSteer));
    }

    if (dir !== 0) {
        const target = dir * tuning.maxAngularSpeed;
        const k = 1 - Math.exp(-tuning.angularAccel * t);
        angularVel += (target - angularVel) * k;
    } else {
        angularVel *= Math.exp(-tuning.angularDrag * t);
        if (Math.abs(angularVel) < 0.04) angularVel = 0;
    }

    const maxW = tuning.maxAngularSpeed;
    if (angularVel > maxW) angularVel = maxW;
    if (angularVel < -maxW) angularVel = -maxW;

    angle += angularVel * t;
    if (angle > Math.PI * 4 || angle < -Math.PI * 4) {
        angle = ((angle + Math.PI) % (Math.PI * 2)) - Math.PI;
    }

    slideSteer *= Math.exp(-18 * t);

    const velSign = orbitVelSign(angularVel);
    if (lastOrbitVelSign !== 0 && velSign !== 0 && lastOrbitVelSign !== velSign) {
        reversalMarks.push({ angle, bornAt: performance.now() });
        if (reversalMarks.length > 28) reversalMarks.shift();
    }
    if (velSign !== 0) lastOrbitVelSign = velSign;

    return orbitCursorPos(canvasW, canvasH, cursorRadiusPx, layoutScale);
}

function orbitVelSign(v: number): number {
    if (v > 0.06) return 1;
    if (v < -0.06) return -1;
    return 0;
}

/** How many balls to spawn this frame from continuous spawn rate. */
export function takeOrbitSpawns(dt: number, score: number): { count: number; ballSpeed: number } {
    const diff = getOrbitDifficulty(score);
    spawnAcc += Math.max(0, diff.spawnRate) * Math.max(0, dt);
    let count = 0;
    while (spawnAcc >= 1 && count < ORBIT_SPAWN_BURST_CAP) {
        spawnAcc -= 1;
        count += 1;
    }
    if (spawnAcc > ORBIT_SPAWN_BURST_CAP) spawnAcc = ORBIT_SPAWN_BURST_CAP;
    return { count, ballSpeed: diff.ballSpeed };
}

/** Edge spawn aiming straight at the canvas center. */
export function pickOrbitInboundSpawn(
    canvasW: number,
    canvasH: number,
    ballR: number,
    speedUnits: number,
): { x: number; y: number; vx: number; vy: number } {
    const cx = canvasW * 0.5;
    const cy = canvasH * 0.5;
    const pad = Math.max(ballR + 4, 8);
    const side = Math.floor(gameRandom() * 4);
    let x = 0;
    let y = 0;
    if (side === 0) {
        x = -pad;
        y = gameRandom() * canvasH;
    } else if (side === 1) {
        x = canvasW + pad;
        y = gameRandom() * canvasH;
    } else if (side === 2) {
        x = gameRandom() * canvasW;
        y = -pad;
    } else {
        x = gameRandom() * canvasW;
        y = canvasH + pad;
    }
    const dx = cx - x;
    const dy = cy - y;
    const len = Math.hypot(dx, dy) || 1;
    const spd = Math.max(0.05, speedUnits);
    return {
        x,
        y,
        vx: (dx / len) * spd,
        vy: (dy / len) * spd,
    };
}

export function orbitBallHitsCore(
    bx: number,
    by: number,
    br: number,
    canvasW: number,
    canvasH: number,
): boolean {
    const cx = canvasW * 0.5;
    const cy = canvasH * 0.5;
    const core = getOrbitCoreRadius(canvasW, canvasH);
    return Math.hypot(bx - cx, by - cy) <= core + br;
}

function drawOrbitMiniCursor(
    ctx: CanvasRenderingContext2D,
    x: number,
    y: number,
    radius: number,
    color: string,
    alpha: number,
): void {
    if (radius <= 0.5 || alpha <= 0.02) return;
    ctx.save();
    ctx.globalAlpha = alpha;
    ctx.fillStyle = color;
    ctx.beginPath();
    ctx.arc(x, y, radius, 0, Math.PI * 2);
    ctx.fill();
    ctx.strokeStyle = "rgba(255,255,255,0.55)";
    ctx.lineWidth = Math.max(0.6, radius * 0.2);
    ctx.beginPath();
    ctx.arc(x, y, radius, 0, Math.PI * 2);
    ctx.stroke();
    ctx.restore();
}

function drawOrbitMiniCross(
    ctx: CanvasRenderingContext2D,
    x: number,
    y: number,
    radius: number,
    alpha: number,
): void {
    if (radius <= 0.5 || alpha <= 0.02) return;
    const arm = radius * 0.82;
    ctx.save();
    ctx.globalAlpha = alpha;
    ctx.strokeStyle = "rgba(255, 80, 70, 0.95)";
    ctx.lineWidth = Math.max(1.2, radius * 0.22);
    ctx.lineCap = "round";
    ctx.beginPath();
    ctx.moveTo(x - arm, y - arm);
    ctx.lineTo(x + arm, y + arm);
    ctx.moveTo(x + arm, y - arm);
    ctx.lineTo(x - arm, y + arm);
    ctx.stroke();
    ctx.restore();
}

function drawOrbitLives(
    ctx: CanvasRenderingContext2D,
    cx: number,
    cy: number,
    coreR: number,
    cursorColor: string,
): void {
    const n = Math.max(1, livesMax);
    /** Size/spacing are fractions of coreR only — same look on phone, tablet, and PC. */
    const span = coreR * (n <= 3 ? 0.92 : n === 4 ? 1.05 : 1.16);
    const spacing = n <= 1 ? 0 : span / (n - 1);
    const lifeR = Math.max(2, coreR * (n >= 5 ? 0.072 : n === 4 ? 0.078 : 0.085));
    const startX = cx - span * 0.5;

    for (let i = 0; i < n; i++) {
        const x = startX + i * spacing;
        const y = cy;
        const lostT = lifeLostProgress[i] ?? 0;
        const cursorA = 1 - lostT;
        const crossA = lostT;
        if (cursorA > 0.02) {
            drawOrbitMiniCursor(ctx, x, y, lifeR * (0.92 + cursorA * 0.08), cursorColor, cursorA);
        }
        if (crossA > 0.02) {
            drawOrbitMiniCross(ctx, x, y, lifeR, crossA);
        }
    }
}

export function drawOrbitCore(
    ctx: CanvasRenderingContext2D,
    canvasW: number,
    canvasH: number,
    strokeColor: string,
    cursorColor = "#66ccff",
    layoutScale = 1,
): void {
    const cx = canvasW * 0.5;
    const cy = canvasH * 0.5;
    const r = getOrbitCoreRadius(canvasW, canvasH);
    const lineW = Math.max(2, Math.min(canvasW, canvasH) * 0.0045);

    ctx.save();

    ctx.beginPath();
    ctx.arc(cx, cy, r, 0, Math.PI * 2);
    ctx.strokeStyle = strokeColor;
    ctx.lineWidth = lineW;
    ctx.shadowColor = strokeColor;
    ctx.shadowBlur = 10;
    ctx.stroke();
    ctx.shadowBlur = 0;

    ctx.beginPath();
    ctx.arc(cx, cy, Math.max(1, r - lineW * 0.85), 0, Math.PI * 2);
    ctx.strokeStyle = "rgba(255,255,255,0.22)";
    ctx.lineWidth = 1;
    ctx.stroke();

    drawOrbitLives(ctx, cx, cy, r, cursorColor);

    ctx.restore();
}

/** Radial fade lines where the orbit cursor reversed spin direction. */
export function drawOrbitReversalMarks(
    ctx: CanvasRenderingContext2D,
    canvasW: number,
    canvasH: number,
    cursorRadiusPx: number,
    layoutScale: number,
    lineColor: string,
    now = performance.now(),
): void {
    if (reversalMarks.length === 0) return;

    const cx = canvasW * 0.5;
    const cy = canvasH * 0.5;
    const orbitR = getOrbitCursorRadius(canvasW, canvasH, cursorRadiusPx, layoutScale);
    const coreR = getOrbitCoreRadius(canvasW, canvasH);
    const lineW = Math.max(1.5, Math.min(canvasW, canvasH) * 0.0025);

    reversalMarks = reversalMarks.filter((m) => now - m.bornAt < ORBIT_REVERSAL_FADE_MS);
    if (reversalMarks.length === 0) return;

    for (const m of reversalMarks) {
        const age = now - m.bornAt;
        const alpha = ORBIT_REVERSAL_ALPHA * (1 - age / ORBIT_REVERSAL_FADE_MS);
        if (alpha <= 0.01) continue;

        const cos = Math.cos(m.angle);
        const sin = Math.sin(m.angle);
        const x0 = cx + cos * orbitR;
        const y0 = cy + sin * orbitR;
        const x1 = cx + cos * coreR;
        const y1 = cy + sin * coreR;

        ctx.save();
        ctx.globalAlpha = alpha;
        ctx.strokeStyle = lineColor;
        ctx.lineWidth = lineW;
        ctx.lineCap = "round";
        ctx.beginPath();
        ctx.moveTo(x0, y0);
        ctx.lineTo(x1, y1);
        ctx.stroke();
        ctx.restore();
    }
}
